import { useLayoutEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import styled from "styled-components";
import gsap from "gsap";
import data from "../data/products.json";
import images from "../assets/products";
import config, { whatsappLink } from "../config";
import Reveal from "../components/Reveal";
import CTABanner from "../components/CTABanner";
import ProductGrid from "../components/ProductGrid";
import { Container, Section, Eyebrow } from "../components/ui";

export default function ProductPage() {
  const { slug } = useParams();
  const product = data.products.find((p) => p.slug === slug);

  const imgRef = useRef(null);
  const introRef = useRef(null);

  useLayoutEffect(() => {
    if (!product) return;
    const ctx = gsap.context(() => {
      gsap.from(".pp-in", {
        y: 26,
        opacity: 0,
        duration: 0.9,
        stagger: 0.08,
        ease: "power3.out",
      });
      gsap.from(imgRef.current, {
        opacity: 0,
        scale: 0.88,
        y: 24,
        duration: 1.1,
        ease: "power3.out",
      });
      gsap.to(imgRef.current, {
        y: -14,
        rotation: 1.6,
        duration: 3.8,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
        delay: 1,
      });
    }, introRef);
    return () => ctx.revert();
  }, [product, slug]);

  if (!product) return <Navigate to="/produktet" replace />;

  return (
    <>
      <Intro ref={introRef} style={{ background: product.accentSoft }}>
        <Container>
          <Crumbs className="pp-in">
            <Link to="/produktet">Produktet</Link>
            <span>/</span>
            <strong>{product.name}</strong>
          </Crumbs>

          <Split>
            <Text>
              <Eyebrow className="pp-in" $color={product.accent}>
                {product.emri}
              </Eyebrow>
              <H1 className="pp-in">{product.name}</H1>
              <Tagline className="pp-in">{product.tagline}</Tagline>
              <Price className="pp-in">
                <strong>{config.price}</strong>
                <em>{config.shipping}</em>
                <em>{config.bundle}</em>
              </Price>
              <Buy
                className="pp-in"
                href={whatsappLink(
                  `Përshëndetje! Dua të porosis Aroma Stick ${product.name}.`
                )}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: product.accent }}
              >
                Porosit në WhatsApp
              </Buy>
            </Text>

            <Figure>
              <img
                ref={imgRef}
                src={images[product.slug]}
                alt={`Aroma Stick ${product.name}`}
              />
            </Figure>
          </Split>
        </Container>
      </Intro>

      <Section>
        <Container>
          <Body>
            <Main>
              <Reveal>
                <Para $lead>{product.intro}</Para>
              </Reveal>

              <Reveal delay={0.05}>
                <Block>
                  <BlockTitle>Për kë është</BlockTitle>
                  <List>
                    {product.forWho.map((f) => (
                      <li key={f}>
                        <Tick style={{ color: product.accent }}>—</Tick>
                        {f}
                      </li>
                    ))}
                  </List>
                </Block>
              </Reveal>

              <Reveal delay={0.05}>
                <Block>
                  <BlockTitle>Si përdoret</BlockTitle>
                  <Para>{product.howToUse}</Para>
                </Block>
              </Reveal>

              <Reveal delay={0.05}>
                <Block>
                  <BlockTitle>Pyetje të shpeshta</BlockTitle>
                  <Accordion items={product.faq} accent={product.accent} />
                </Block>
              </Reveal>
            </Main>

            <Side>
              <Reveal delay={0.1}>
                <Panel>
                  <PanelTitle>Përbërësit</PanelTitle>
                  <Ingredients>
                    {product.ingredients.map((ing) => (
                      <li key={ing}>
                        <Leaf style={{ background: product.accent }} />
                        {ing}
                      </li>
                    ))}
                  </Ingredients>

                  <Divider />

                  <Fact>
                    <FactKey>Zgjatja</FactKey>
                    <FactVal>{product.duration}</FactVal>
                  </Fact>
                  <Fact>
                    <FactKey>Origjina</FactKey>
                    <FactVal>Prodhuar në Zvicër</FactVal>
                  </Fact>
                  <Fact>
                    <FactKey>Certifikimi</FactKey>
                    <FactVal>Vajra esencialë 100% organikë</FactVal>
                  </Fact>
                </Panel>
              </Reveal>
            </Side>
          </Body>
        </Container>
      </Section>

      <Section $alt $tight>
        <Container>
          <Reveal>
            <OtherTitle>Variantet e tjera</OtherTitle>
          </Reveal>
          <ProductGrid exclude={product.slug} />
        </Container>
      </Section>

      <Spacer />
      <CTABanner accent={product.accent} product={product.name} />
    </>
  );
}

/* ---------------------------- accordion ---------------------------- */

function Accordion({ items, accent }) {
  const [open, setOpen] = useState(0);

  return (
    <AccWrap>
      {items.map((it, i) => (
        <AccItem key={it.q}>
          <AccBtn
            onClick={() => setOpen(open === i ? -1 : i)}
            aria-expanded={open === i}
          >
            <span>{it.q}</span>
            <Sign $open={open === i} style={{ color: accent }}>
              +
            </Sign>
          </AccBtn>
          <AccBody $open={open === i}>
            <p>{it.a}</p>
          </AccBody>
        </AccItem>
      ))}
    </AccWrap>
  );
}

/* ----------------------------- styles ----------------------------- */

const Intro = styled.header`
  padding: 110px 0 56px;
  transition: background 0.6s ease;

  ${({ theme }) => theme.bp.md} {
    padding: 148px 0 72px;
  }
`;

const Crumbs = styled.nav`
  display: flex;
  gap: 9px;
  font-size: ${({ theme }) => theme.size.xs};
  color: ${({ theme }) => theme.colors.inkSoft};
  margin-bottom: 36px;

  a:hover {
    text-decoration: underline;
  }
  strong {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.ink};
  }
`;

const Split = styled.div`
  display: grid;
  gap: 32px;
  grid-template-columns: 1fr;
  align-items: center;

  ${({ theme }) => theme.bp.md} {
    grid-template-columns: 1.1fr 0.9fr;
    gap: 56px;
  }
`;

const Text = styled.div`
  order: 2;
  ${({ theme }) => theme.bp.md} {
    order: 1;
  }
`;

const H1 = styled.h1`
  font-size: ${({ theme }) => theme.size.h1};
  margin-bottom: 14px;
`;

const Tagline = styled.p`
  font-size: ${({ theme }) => theme.size.lg};
  color: ${({ theme }) => theme.colors.inkSoft};
  max-width: 34ch;
  margin-bottom: 28px;
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
  margin-bottom: 30px;

  strong {
    font-family: ${({ theme }) => theme.font.display};
    font-size: ${({ theme }) => theme.size.xl};
    font-weight: 500;
  }
  em {
    font-style: normal;
    font-size: ${({ theme }) => theme.size.xs};
    padding: 6px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.6);
    color: ${({ theme }) => theme.colors.inkSoft};
  }
`;

const Buy = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 16px 34px;
  border-radius: 999px;
  color: #fff;
  font-weight: 500;
  font-size: ${({ theme }) => theme.size.sm};
  box-shadow: 0 12px 28px -12px rgba(28, 27, 24, 0.55);
  transition: transform 0.35s ${({ theme }) => theme.ease};

  &:hover {
    transform: translateY(-2px);
  }
`;

const Figure = styled.div`
  order: 1;
  display: grid;
  place-items: center;
  min-height: 240px;

  ${({ theme }) => theme.bp.md} {
    order: 2;
    min-height: 420px;
  }

  img {
    max-height: 240px;
    width: auto;
    filter: drop-shadow(0 34px 44px rgba(28, 27, 24, 0.3));
    will-change: transform;

    ${({ theme }) => theme.bp.md} {
      max-height: 420px;
    }
  }
`;

const Body = styled.div`
  display: grid;
  gap: 48px;
  grid-template-columns: 1fr;

  ${({ theme }) => theme.bp.md} {
    grid-template-columns: 1.35fr 0.65fr;
    gap: 72px;
  }
`;

const Main = styled.div``;

const Block = styled.div`
  margin-top: 52px;
`;

const BlockTitle = styled.h2`
  font-size: ${({ theme }) => theme.size.h3};
  margin-bottom: 20px;
`;

const Para = styled.p`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme, $lead }) => ($lead ? theme.size.lg : theme.size.base)};
  line-height: 1.75;
  max-width: 62ch;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 13px;

  li {
    display: flex;
    gap: 13px;
    color: ${({ theme }) => theme.colors.inkSoft};
    max-width: 58ch;
  }
`;

const Tick = styled.span`
  flex-shrink: 0;
  font-weight: 600;
`;

const Side = styled.aside``;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 32px 28px 34px;
`;

const PanelTitle = styled.h3`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.size.xs};
  text-transform: uppercase;
  letter-spacing: 0.16em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.inkFaint};
  margin-bottom: 18px;
`;

const Ingredients = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: ${({ theme }) => theme.size.sm};
  }
`;

const Leaf = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  margin: 26px 0 22px;
`;

const Fact = styled.div`
  margin-bottom: 16px;
`;

const FactKey = styled.div`
  font-size: ${({ theme }) => theme.size.xs};
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: ${({ theme }) => theme.colors.inkFaint};
  margin-bottom: 4px;
`;

const FactVal = styled.div`
  font-size: ${({ theme }) => theme.size.sm};
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const AccWrap = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.line};
  max-width: 62ch;
`;

const AccItem = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.line};
`;

const AccBtn = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 0;
  text-align: left;
  font-size: ${({ theme }) => theme.size.base};
  font-weight: 500;
`;

const Sign = styled.span`
  flex-shrink: 0;
  font-size: 1.4rem;
  line-height: 1;
  transition: transform 0.4s ${({ theme }) => theme.ease};
  transform: rotate(${({ $open }) => ($open ? "135deg" : "0deg")});
`;

const AccBody = styled.div`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.45s ${({ theme }) => theme.ease};

  p {
    overflow: hidden;
    color: ${({ theme }) => theme.colors.inkSoft};
    font-size: ${({ theme }) => theme.size.sm};
    padding-bottom: ${({ $open }) => ($open ? "22px" : "0")};
    transition: padding 0.45s ${({ theme }) => theme.ease};
  }
`;

const OtherTitle = styled.h2`
  font-size: ${({ theme }) => theme.size.h3};
  margin-bottom: 36px;
`;

const Spacer = styled.div`
  height: 88px;

  ${({ theme }) => theme.bp.md} {
    height: 136px;
  }
`;
